import { Icon } from '@iconify/react';
import React from 'react';
import { useReveal } from '../hooks/useReveal';

interface Project {
	title: string; desc: string; images: string[];
	repo_link?: string; demo_link?: string; tags?: string[];
}

const projects: Project[] = [
	{
		title: 'Fyyur', tags: ['Python', 'Flask', 'PostgreSQL'],
		desc: 'A musical venue and artist booking site facilitating discovery and bookings of shows between local performing artists and venues.',
		repo_link: 'https://github.com/Kimbohlovette/Fyyur',
		images: [require('../assets/image-fyyur.png')],
	},
	{
		title: 'Trivia', tags: ['React', 'Python', 'REST API'],
		desc: 'An interactive trivia game where students answer questions by category or across all categories with a live score tracker.',
		repo_link: 'https://github.com/Kimbohlovette/Trivia',
		demo_link: 'https://trivia-two-topaz.vercel.app',
		images: [require('../assets/image-trivia.png')],
	},
	{
		title: 'WhoGet', tags: ['React Native', 'Next.js', 'Node.js'],
		desc: 'A mobile app connecting people who need goods and services to those who have them. Admin dashboard built with Next.js.',
		repo_link: 'https://github.com/Kimbohlovette/whoget-mobile-client',
		images: [require('../assets/image-whoget.png')],
	},
	{
		title: 'Farmino', tags: ['React', 'Node.js', 'MongoDB'],
		desc: 'An e-farm platform where farmers list and showcase items, and buyers can browse, read about, and purchase products.',
		repo_link: 'https://github.com/Kimbohlovette/farmino',
		demo_link: 'https://farmino.netlify.app/',
		images: [require('../assets/image-farmino.png')],
	},
	{
		title: 'Iknite Calculator', tags: ['JavaScript', 'CSS'],
		desc: 'A clean, functional business calculator built with vanilla JavaScript and CSS — no frameworks.',
		repo_link: 'https://github.com/Kimbohlovette/iknitespace-exercises/tree/main/iknite-ex5',
		demo_link: 'https://iknite-calculator.netlify.app/',
		images: [require('../assets/image-calculator.png')],
	},
	{
		title: 'Iknite Portfolio Manager', tags: ['React', 'TypeScript'],
		desc: 'A React app for showcasing Iknite Space projects, events, and members\' portfolios.',
		repo_link: 'https://github.com/Kimbohlovette/iknite-portfolio-manager',
		demo_link: 'https://iknite-portfolio-manager.netlify.app/members',
		images: [require('../assets/image-ikinte-portfolio.png')],
	},
];

const ProjectCard = ({ title, images, repo_link, demo_link, desc, tags, index }: Project & { index: number }) => {
	const ref = useReveal();
	return (
		<div ref={ref} className={`reveal reveal-delay-${Math.min((index % 3) + 1, 4)} group glass-card overflow-hidden hover:shadow-card-hover hover:border-accent/30 hover:-translate-y-1 transition-all duration-300`}>
			<div className="overflow-hidden h-44 bg-slate-800 relative">
				<img src={images[0] ?? require('../assets/projectImage.jpg')} alt={title}
					className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100" />
				<div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
				{/* Links overlay */}
				<div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
					{repo_link && (
						<a href={repo_link} target="_blank" rel="noreferrer" aria-label="GitHub"
							className="w-8 h-8 rounded-lg glass-card flex items-center justify-center text-white hover:text-accent transition-colors">
							<Icon icon="mdi:github" fontSize={16} />
						</a>
					)}
					{demo_link && (
						<a href={demo_link} target="_blank" rel="noreferrer" aria-label="Live demo"
							className="w-8 h-8 rounded-lg glass-card flex items-center justify-center text-white hover:text-accent-2 transition-colors">
							<Icon icon="mdi:open-in-new" fontSize={16} />
						</a>
					)}
				</div>
			</div>
			<div className="p-5">
				<h3 className="font-display font-semibold text-white text-base mb-2">{title}</h3>
				<p className="text-sm text-slate-400 line-clamp-2 leading-relaxed mb-4">{desc}</p>
				<div className="flex flex-wrap gap-1.5">
					{tags?.map(t => (
						<span key={t} className="text-xs px-2 py-0.5 rounded-md bg-slate-800 text-slate-500 border border-slate-700">{t}</span>
					))}
				</div>
			</div>
		</div>
	);
};

function Portfolio() {
	return (
		<section className="max-w-6xl mx-auto px-6">
			<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
				{projects.map((p, i) => <ProjectCard key={i} {...p} index={i} />)}
			</div>
		</section>
	);
}

export default Portfolio;
